import type { FC } from 'react'
import React, { useMemo, useState } from 'react'

import type { ValidationError } from '@skytech/auth'
import { config } from '@skytech/config'
import type { Locale } from '@skytech/types'
import type { ValueLabelPair } from '@skytech/ui'
import { Input, LoaderCircle, MessagePopup, Select, SuccessMessage } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { availableLanguages } from '@skytech/ui/LanguageChoiceButtons'
import { COUNTRY_FLAGS, countryOptions, getDefaultCountryId, LANGUAGE_FLAGS } from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field, Form, FormikProvider, useFormik } from 'formik'
import { string, object, number } from 'yup'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { DEFAULT_LOCALE } from '@/app-constants'
import useUser from '@/hooks/user'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'

import { userGroupOptions } from './selectOptions'

type Values = {
  id?: number
  countryId: number | string
  userGroupId: number | string
  customerId: number | string
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  password?: string
  locale?: Locale
}

type MutateParams = {
  body: Values
}

type Props = {
  userId: number
  customers: ValueLabelPair[]
  onClose: () => void
}

export const UserForm: FC<Props> = ({ userId, customers, onClose }) => {
  const isDevelopmentMode = config.ENVIRONMENT === 'dev'

  const user = useUser()

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  if (isNaN(userId)) {
    throw new Error('User ID must be a number')
  }

  const [showGeneratedPasswordMessage, setShowGeneratedPasswordMessage] = useState(false)

  const queryClient = useQueryClient()
  const { locale, setLocale } = useAppContext()
  const translate = useTranslation()
  const requiredMessage = translate('This field is mandatory')
  const validationSchema = object({
    firstName: string().required(requiredMessage),
    lastName: string().required(requiredMessage),
    phoneNumber: string().required(requiredMessage),
    password: userId ? string().min(8) : string().optional(),
    email: string().email().required(requiredMessage),
    countryId: number().required(requiredMessage),
    userGroupId: number().required(requiredMessage),
    customerId: number().required(requiredMessage),
    locale: string().required(requiredMessage),
  })

  // Get user by id
  const { isFetching, data: userDetails } = useQuery(
    queries.adminUserDetails(userId),
    () => adminResources.getUserDetails(userId),
    {
      cacheTime: 0,
      enabled: Boolean(userId),
    }
  )
  const defaultPhoneCountryId = getDefaultCountryId(locale)
  const initialValues: Values = {
    firstName: userDetails?.firstName || '',
    lastName: userDetails?.lastName || '',
    phoneNumber: userDetails?.phoneNumber || '',
    email: userDetails?.email || '',
    userGroupId: userDetails?.userGroupId || '',
    countryId: userDetails?.countryId || defaultPhoneCountryId,
    customerId: userDetails?.customerId || '',
    locale: (userDetails?.locale as Locale) || locale,
  }

  const upsert = ({ body }: MutateParams) => {
    if (userDetails?.id) {
      return adminResources.updateUser(
        {
          ...body,
          id: userDetails.id,
          // todo: improve
          countryId: body.countryId ? Number(body.countryId) : undefined,
          userGroupId: body.userGroupId ? Number(body.userGroupId) : undefined,
          customerId: body.customerId ? Number(body.customerId) : undefined,
          locale: body.locale ? body.locale : undefined,
        },
        userDetails?.id
      )
    }
    return adminResources.createUser({
      ...body,
      // todo: improve
      countryId: Number(body.countryId),
      userGroupId: Number(body.userGroupId),
      customerId: Number(body.customerId),
    })
  }

  const { mutate: deleteUser, isLoading: isLoadingDelete } = useMutation(
    adminResources.deleteUser,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queries.adminUsersList())
        onClose()
      },
    }
  )

  const onGeneratePassword = () => {
    if (userDetails?.id) generatePassword(userDetails.id)
  }

  const hasEditPermission = useMemo(() => {
    // For new users or if we are editing our own user we have edit permission
    if (!userDetails?.id || userDetails.id === user?.id) return true

    // For existing users we have edit permission if our role is lower than the user we are editing
    const currentUserRole = userGroupOptions.find(userGroup => userGroup.name === user?.role)!
    const resourceRole = userGroupOptions.find(
      userGroup => userGroup.name === userDetails!.userGroup!.name
    )!

    // lower role value has higher priority
    return resourceRole.value !== 1 && currentUserRole.value <= resourceRole.value
  }, [userDetails, user])

  const filteredUserGroupOptions = useMemo(() => {
    if (!user?.role) return []

    return userGroupOptions.filter(option => {
      const currentUserRole = userGroupOptions.find(userGroup => userGroup.name === user?.role)
      if (!currentUserRole) throw Error('No role found!')

      // Mobile user should only be avialable in the usergroup drowdown if that's the current usergroup of the user.
      if (option.name === 'mobile_user' && currentUserRole.name !== 'mobile_user') return false
      // Seller should only be avialable in the usergroup drowdown if that's the current usergroup of the user.
      if (option.name === 'seller' && currentUserRole.name !== 'seller') return false

      // if the option is not deprecated and its value is bigger than the current user role value we show it
      return !option.deprecated && option.value >= currentUserRole.value && option.value !== 1
    })
  }, [user])

  const formik = useFormik({
    validationSchema: validationSchema,
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: values => {
      if (hasEditPermission) {
        mutate({ body: values })
      }
    },
  })

  // Submit form action
  const { mutate, isLoading: isLoadingUserMutation } = useMutation(upsert, {
    onSuccess(_data, { body }) {
      if (userDetails?.id === user?.id) {
        setLocale(body?.locale || DEFAULT_LOCALE)
      }
      queryClient.invalidateQueries(queries.adminUsersList())
      onClose()
    },
    onError(error: ValidationError) {
      // Status 422 is a validation error from server
      // Apart from standard validation we also check for number unique validation
      if (error?.status === 422 && error.json) {
        const formikErrors: Record<string, string> = {}
        Object.keys(error.json).forEach(key => {
          formikErrors[key] = error.json?.[key]?.join(', ')
        })

        formik.setErrors(formikErrors)
      }
    },
  })

  const {
    mutate: generatePassword,
    isLoading: isLoadingGeneratePassword,
    isSuccess,
  } = useMutation(adminResources.generateUserPassword, {
    onSuccess() {
      setShowGeneratedPasswordMessage(true)
      setTimeout(() => {
        setShowGeneratedPasswordMessage(false)
      }, 10000)
    },
  })

  const isLoading = isLoadingUserMutation || isLoadingDelete || isLoadingGeneratePassword

  // when refetching data
  if (isFetching || !user?.id) {
    return (
      <div className="min-h-[60px]">
        <LoaderCircle />
      </div>
    )
  }

  return (
    <>
      <h2>
        <Translation id={userDetails?.id ? 'Edit User' : 'Add User'} />
      </h2>
      <FormikProvider value={formik}>
        <Form>
          <div className="grid grid-cols-2 gap-4">
            <Field name="firstName">
              {({ field, meta }: FieldProps) => (
                <Input
                  isFullWidth
                  capitalizeLabel
                  id="firstName"
                  label={
                    <>
                      <Translation id="First name" />*
                    </>
                  }
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  isDisabled={!hasEditPermission}
                  {...field}
                />
              )}
            </Field>
            <Field name="lastName">
              {({ field, meta }: FieldProps) => (
                <Input
                  isFullWidth
                  capitalizeLabel
                  id="lastName"
                  label={
                    <>
                      <Translation id="Last name" />*
                    </>
                  }
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  isDisabled={!hasEditPermission}
                  {...field}
                />
              )}
            </Field>
          </div>
          <Field name="email">
            {({ field, meta }: FieldProps) => (
              <Input
                isFullWidth
                capitalizeLabel
                id="email"
                label={
                  <>
                    <Translation id="Email" />*
                  </>
                }
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                isDisabled={!hasEditPermission}
                {...field}
              />
            )}
          </Field>
          <div className="flex gap-2">
            <Field name="countryId">
              {({ field, meta, form }: FieldProps) => (
                <Select
                  capitalizeLabel
                  id="countryId"
                  label={
                    <>
                      <Translation id="Country" />*
                    </>
                  }
                  options={countryOptions.map(option => ({
                    value: option.value,
                    label: `${COUNTRY_FLAGS[option.label]} +${option.value}`,
                  }))}
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  isDisabled={!hasEditPermission}
                  {...field}
                  onChange={option => form.setFieldValue(field.name, option?.value)}
                />
              )}
            </Field>
            <Field name="phoneNumber">
              {({ field, meta }: FieldProps) => (
                <Input
                  isFullWidth
                  wrapperClassName="grow"
                  capitalizeLabel
                  id="phoneNumber"
                  label={
                    <>
                      <Translation id="Phone number" />*
                    </>
                  }
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  isDisabled={!hasEditPermission}
                  {...field}
                />
              )}
            </Field>

            <Field name="locale">
              {({ field, meta, form }: FieldProps) => (
                <Select
                  capitalizeLabel
                  id="locale"
                  label={
                    <>
                      <Translation id="Language:" />*
                    </>
                  }
                  options={availableLanguages.map(lang => ({
                    value: lang,
                    label: `${LANGUAGE_FLAGS[lang]} - ${lang.toUpperCase()}`,
                  }))}
                  isDisabled={!hasEditPermission}
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  {...field}
                  onChange={option => form.setFieldValue(field.name, option?.value)}
                />
              )}
            </Field>
          </div>
          <div className="w-full">
            <Field name="customerId">
              {({ field, meta, form }: FieldProps) => (
                <Select
                  capitalizeLabel
                  id="customerId"
                  className="w-full"
                  label={
                    <>
                      <Translation id="Company" />*
                    </>
                  }
                  options={customers}
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  isDisabled={!hasEditPermission}
                  {...field}
                  onChange={option => form.setFieldValue(field.name, option?.value)}
                />
              )}
            </Field>
          </div>
          <div className="w-full">
            <Field name="userGroupId">
              {({ field, meta, form }: FieldProps) => (
                <Select
                  capitalizeLabel
                  id="userGroupId"
                  className="w-full"
                  label={
                    <>
                      <Translation id="User Group" />*
                    </>
                  }
                  options={filteredUserGroupOptions}
                  marginBottom={16}
                  isDisabled={!hasEditPermission}
                  touched={meta.touched}
                  error={meta.error}
                  {...field}
                  onChange={option => form.setFieldValue(field.name, option?.value)}
                />
              )}
            </Field>
          </div>

          {/* TODO: enable for production */}
          {userDetails?.id && isDevelopmentMode && (
            <div className="w-full">
              <Field name="password">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    capitalizeLabel
                    isDisabled={!hasEditPermission}
                    id="password"
                    type="password"
                    label={<Translation id="Password" />}
                    marginBottom={16}
                    touched={meta.touched}
                    error={meta.error}
                    {...field}
                  />
                )}
              </Field>
            </div>
          )}
          {isSuccess && showGeneratedPasswordMessage && (
            <SuccessMessage>
              <Translation id="New password generated and sent to user" />
            </SuccessMessage>
          )}
          <footer className="flex justify-end">
            <Button variant="tertiary" onClick={onClose} disabled={isLoading}>
              <Translation id="Cancel" />
            </Button>

            {/* TODO: enable for production */}
            {userDetails?.id && hasEditPermission && isDevelopmentMode && (
              <Button
                onClick={onGeneratePassword}
                isLoading={isLoading}
                disabled={showGeneratedPasswordMessage}
              >
                <Translation id="Generate password" />
              </Button>
            )}

            {userDetails?.id && hasEditPermission && (
              <Button
                variant="secondary"
                onClick={() => setIsDeleteOpen(true)}
                isLoading={isLoading}
              >
                <Translation id="delete" />
              </Button>
            )}

            {hasEditPermission && (
              <Button variant="primary" type="submit" isLoading={isLoading}>
                <Translation id="Submit" />
              </Button>
            )}
          </footer>
        </Form>
      </FormikProvider>

      <MessagePopup
        title={<Translation id="Are you absolutely sure?" />}
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        message={
          <div>
            <p>
              This action cannot be undone. This will permanently delete the selected user:{' '}
              {userDetails?.firstName} {userDetails?.lastName} ({userDetails?.id}).
            </p>

            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setIsDeleteOpen(false)}>
                <Translation id="Cancel" />
              </Button>

              <Button
                variant="primary"
                onClick={() => userDetails?.id && deleteUser(userDetails.id)}
              >
                <Translation id="Continue" />
              </Button>
            </div>
          </div>
        }
      />
    </>
  )
}
